import { hashKey, type QueryClient } from '@tanstack/react-query';
import { ApiError, type components, type Panel } from '../api';
import { readMe, sessionQueryKey, type AuthDeps, type Principal } from './session';

export type LoginCredentials =
  | components['schemas']['LoginRequest']
  | components['schemas']['AdminVerifyRequest']
  | components['schemas']['VerifyTotpRequest'];
export type LoginResult =
  | { status: 'signed_in'; principal: Principal; recoveryCodes?: string[] }
  | {
      status: 'second_factor';
      ticket: string;
      expiresAt: string;
      totpSetup?: { otpauth_uri: string; secret: string };
    };

// Both panels log in in two steps (D-044, D-131; UI architecture §6; Merchant API §3.1; admin API
// §1.3): the e-mail and password answer a ticket, then the ticket with a TOTP or recovery code
// signs in; each panel calls login() once per step. With the second factor switched off, step 1
// signs in at once. The panel's own paths come from api.panel; a screen never names one.
export async function login(deps: AuthDeps, input: LoginCredentials): Promise<LoginResult> {
  const { api } = deps;
  if ('ticket' in input) {
    // The client rejects every non-2xx answer with an ApiError, so a returned call has its data.
    const { data } =
      api.panel === 'merchant'
        ? await api.fetchClient.POST('/api/auth/2fa', { body: input })
        : await api.fetchClient.POST('/admin/api/auth/2fa', { body: input });
    const principal = await signIn(deps);
    const recoveryCodes = data!.recovery_codes;
    // recoveryCodes is there only on the answer that committed an enrolment (shown once).
    return recoveryCodes
      ? { status: 'signed_in', principal, recoveryCodes }
      : { status: 'signed_in', principal };
  }
  if (api.panel === 'admin') {
    const { data } = await api.fetchClient.POST('/admin/api/auth/login', { body: input });
    if (data!.signed_in) return { status: 'signed_in', principal: await signIn(deps) };
    return secondFactor('admin', data!.ticket, data!.expires_at, data!.totp_setup);
  }
  const { data } = await api.fetchClient.POST('/api/auth/login', { body: input });
  if (data!.signed_in) return { status: 'signed_in', principal: await signIn(deps) };
  return secondFactor('merchant', data!.ticket, data!.ticket_expires_at, data!.totp_setup);
}

// Step 1 sets no session: the ticket goes to the screen. An answer with neither signed_in nor a
// ticket is a bug the screen must see, never a silent second_factor without a ticket.
function secondFactor(
  panel: Panel,
  ticket: string | undefined,
  expiresAt: string | undefined,
  totpSetup: { otpauth_uri: string; secret: string } | undefined,
): LoginResult {
  if (!ticket || !expiresAt)
    throw new Error(`${panel} login answered neither signed_in nor a ticket`);
  return totpSetup
    ? { status: 'second_factor', ticket, expiresAt, totpSetup }
    : { status: 'second_factor', ticket, expiresAt };
}

// Clears every query but the session's, and every mutation. The session query stays in the cache,
// so a mounted useSession() follows the principal set after this; clear() would drop it.
function clearCache(queryClient: QueryClient, panel: Panel) {
  const sessionHash = hashKey(sessionQueryKey(panel));
  queryClient.removeQueries({ predicate: (query) => query.queryHash !== sessionHash });
  queryClient.getMutationCache().clear();
}

// A new session keeps nothing of the previous one (UI architecture §6), and GET /me fills the
// principal (merchant screens §3.1; admin screens §3.1).
async function signIn({ api, queryClient }: AuthDeps): Promise<Principal> {
  clearCache(queryClient, api.panel);
  const principal = await readMe(api);
  queryClient.setQueryData(sessionQueryKey(api.panel), principal);
  return principal;
}

// Logging out clears the query cache and the session, also when the call fails (UI architecture
// §5; admin screens §3.1); the error is rethrown after. A 401 means the session is already gone
// (another tab logged out first), which is the result logout wants.
export async function logout({ api, queryClient }: AuthDeps): Promise<void> {
  try {
    if (api.panel === 'merchant') await api.fetchClient.POST('/api/auth/logout');
    else await api.fetchClient.POST('/admin/api/auth/logout');
  } catch (error) {
    if (!(error instanceof ApiError && error.type === 'unauthenticated')) throw error;
  } finally {
    clearCache(queryClient, api.panel);
    queryClient.setQueryData(sessionQueryKey(api.panel), null);
  }
}
